import {
  ActivityType,
  CoveredLegs,
  EffectiveRegistrationStatus,
  EffectiveSourceKind,
  DSAttendanceMonitoringType,
  ACTIVITY_DESCRIPTION_MIN_LENGTH,
  ACTIVITY_ATTACHMENT_LIMITS,
  formatMonitoringTypeLabel
} from '@/constants/ds-attendance.constants'
import { DEFAULT_UTC_OFFSET, buildIsoAtOffset, formatHourMinuteInOffset } from '@/utils/date'
import type {
  ActivityRecordCandidateStation,
  ActivityRecordWarning
} from '@/Network/Services/DsAttendanceService'
import type { DsAttendanceUploadForm } from '@/components/designSystem/modals/AttendanceUploadModal/types'
import type { ActivitySelfCheckState } from './activity-self-check'

/**
 * M2 — modal "Enviar registro".
 *
 * Além dos tipos do estado do wizard, este arquivo concentra os **helpers puros**
 * do fluxo (normalização do prefill, horários, tolerância, catálogo de erros).
 * Eles moram aqui de propósito: o plano da F2 fecha a lista de arquivos da task
 * em 7 componentes + `activity-self-check.ts` + este, e um helper dentro de um
 * `.vue` não seria testável sem montar componente.
 */

/* ------------------------------------------------------------------------- *
 * Passos e categorias
 * ------------------------------------------------------------------------- */

/**
 * 5 passos + a tela de sucesso. `SUCCESS` não aparece no stepper: ele é o
 * desfecho do passo 5, no mesmo espírito do `STEP.WARNINGS` do wizard de folha.
 */
export const SEND_RECORD_STEP = {
  CATEGORY: 1,
  STATIONS: 2,
  TYPE_AND_FILES: 3,
  EQUIVALENCE: 4,
  REVIEW: 5,
  SUCCESS: 6
} as const

export type SendRecordStep = (typeof SEND_RECORD_STEP)[keyof typeof SEND_RECORD_STEP]

export const SEND_RECORD_STEP_LABELS: Record<number, string> = {
  [SEND_RECORD_STEP.CATEGORY]: 'Categoria',
  [SEND_RECORD_STEP.STATIONS]: 'Plantões',
  [SEND_RECORD_STEP.TYPE_AND_FILES]: 'Tipo e anexos',
  [SEND_RECORD_STEP.EQUIVALENCE]: 'Equivalência',
  [SEND_RECORD_STEP.REVIEW]: 'Revisão'
}

/** Bifurcação do passo 1 (§2.1). `FOLHA` reencaminha ao wizard que já existe. */
export type SendRecordCategory = 'FOLHA' | 'ATIVIDADE'

/** Subtipo do Registro de Atividade. `PRODUTIVIDADE` fica visível e desabilitado. */
export type ActivitySubtype = 'JORNADA' | 'PRODUTIVIDADE'

/** As 3 modalidades de Registro de Plantão oferecidas no passo 1. */
export const SEND_RECORD_SHEET_OPTIONS: Array<{
  value: DSAttendanceMonitoringType
  label: string
  hint?: string
}> = [
  {
    value: DSAttendanceMonitoringType.FOLHA,
    label: 'Folha de monitoramento',
    hint: 'folha diária, com checklist de conferência'
  },
  { value: DSAttendanceMonitoringType.NON_DAILY_SHEET, label: 'Folha não diária' },
  { value: DSAttendanceMonitoringType.EXTERNAL_APP, label: 'App Externo' }
]

/* ------------------------------------------------------------------------- *
 * Contexto de entrada
 * ------------------------------------------------------------------------- */

/** Uma linha da lista de plantões do passo 2. */
export interface SendRecordStationOption {
  doctorStationId: number
  /** Data local do plantão (ISO `yyyy-MM-dd` ou data completa). */
  referenceDate?: string | null
  /** Previsto do plantão — é o que pré-preenche a equivalência (Q5). */
  expectedStart?: string | null
  expectedEnd?: string | null
  doctorName?: string | null
  /** Escala do plantão — permite o filtro defensivo por escala da origem (BF16 fix). */
  scheduleId?: number | null
  scheduleName?: string | null
  healthUnitName?: string | null
  shiftName?: string | null
  /** Turno da vaga — vira o turno pré-selecionado no wizard de folha (#3079). */
  shiftId?: number | null
  registrationStatus?: EffectiveRegistrationStatus | null
  /** Perna que falta, quando quem abre o modal já sabe (M1). */
  missingLegs?: CoveredLegs | null
  /** Fuso da escala/unidade (`-03:00`), quando quem abre o modal já o tem. */
  timezone?: string | null
  /**
   * BF16 — já existe um Registro de Atividade aguardando auditoria para este
   * plantão. Some junto com o chip de status: enviar de novo cai no 409
   * `ACTIVITY_RECORD_ALREADY_OPEN` (§2.7), e o aviso aqui evita o clique.
   */
  hasOpenActivityRecord?: boolean
}

/**
 * Tudo o que o modal precisa da vaga de origem. Quem abre (FAB de M1 em W2,
 * menu da fila em W5) monta este objeto; o modal nunca inventa `companyId`,
 * `healthUnitId` nem `scheduleId` — eles vão no reencaminhamento ao wizard de
 * folha e na chave de permissão do envio.
 */
export interface SendRecordContext {
  doctorStationId: number
  /**
   * Modalidade e regra de folha da política VIGENTE da escala (de
   * `GET registration-settings`). Ausentes, o passo 1 segue oferecendo as duas
   * categorias, como antes desta regra existir.
   */
  policyMonitoringType?: DSAttendanceMonitoringType | null
  hasSheetRuleInEffect?: boolean | null
  scheduleId: number
  companyId?: number | null
  healthUnitId?: number | null
  referenceDate?: string | null
  /** Dados de exibição da vaga de origem (cabeçalho e linha travada do passo 2). */
  station?: SendRecordStationOption | null
  /**
   * Perfil de backoffice/auditoria: é o único que enxerga "Ajuste
   * administrativo". Ausente, o modal resolve pela permissão
   * `attendance_basic_receiving`.
   */
  isBackofficeAudit?: boolean
  /** Catálogo aceito pela política da escala. Vazio/ausente = tudo aceito. */
  acceptedActivityTypes?: ActivityType[]
  checkinLateToleranceMinutes?: number | null
  checkoutEarlyToleranceMinutes?: number | null
}

/**
 * Texto único do bloqueio do "Registro de Plantão" no passo 1. Vive aqui porque o
 * modal e o hub da fila dizem a MESMA coisa — e um dos dois com outra frase seria
 * a pessoa achando que são regras diferentes.
 */
export const SHEET_BLOCKED_BY_APP_POLICY_REASON =
  'Esta escala registra pelo App Minha Escala. Envie um Registro de Atividade.'

/**
 * A folha do turno deste plantão já foi enviada: um novo envio cairia no 409 "Este
 * registro já possui uma folha de presença enviada" DEPOIS do checklist (#3079).
 */
export const SHEET_ALREADY_SUBMITTED_REASON =
  'A folha deste turno já foi enviada. Para trocá-la, a Auditoria precisa reverter a validação ou reprovar pedindo uma nova folha.'

/**
 * Caso real de PROD (escala ANESTESIOLOGIA, Parelheiros): a política vigente era
 * MINHA_ESCALA_APP, sem regra de folha, e o escalista só descobria depois de subir
 * o anexo — o backend respondia "Nenhum container de presença encontrado para a
 * escala…". Com a modalidade em mãos, a opção nem chega a ser clicável.
 *
 * Só bloqueia com informação NA MÃO: backend antigo (campos ausentes) ou consulta
 * sem escala no escopo (`monitoringType` nulo E `hasSheetRuleInEffect` nulo)
 * mantêm o comportamento de hoje. Bloquear por omissão esconderia a folha de
 * escalas que a usam.
 */
export function isSheetCategoryBlocked(
  settings?: {
    monitoringType?: DSAttendanceMonitoringType | null
    hasSheetRuleInEffect?: boolean | null
  } | null
): boolean {
  if (!settings) return false

  const { monitoringType, hasSheetRuleInEffect } = settings
  if (monitoringType === DSAttendanceMonitoringType.MINHA_ESCALA_APP) return true

  // Modalidade de folha diária, mas nenhuma regra da política cai nesta data: o
  // cron não criou container e o envio morreria no mesmo 404.
  if (monitoringType === DSAttendanceMonitoringType.FOLHA) return hasSheetRuleInEffect === false

  // `NON_DAILY_SHEET` e `EXTERNAL_APP` têm envio próprio e o backend devolve
  // `hasSheetRuleInEffect: false` para elas por definição — ler isso como bloqueio
  // esconderia "Folha não diária" e "App Externo" justamente de quem os usa.
  if (monitoringType) return false

  // Sem modalidade: só bloqueia se o backend AFIRMOU que não há regra de folha
  // (escala sem política vigente). Campo ausente segue liberando tudo.
  return hasSheetRuleInEffect === false
}

/** Payload de `redirect-to-sheet` — o que o wizard de folha precisa pré-preenchido. */
export interface RedirectToSheetPayload {
  companyId: number | null
  healthUnitId: number | null
  referenceDate: string
  scheduleId: number
  monitoringType: DSAttendanceMonitoringType
  /** Turno da vaga de origem, quando quem abriu o modal o conhece. */
  shiftId?: number | null
}

/** O que quem recebe `redirect-to-sheet` passa ao `AttendanceUploadModal` (§8.2). */
export interface SheetHandoff {
  form: Partial<DsAttendanceUploadForm>
  lockedFields: string[]
}

/**
 * `yyyy-MM-dd` vira a data LOCAL do dia — nunca `new Date('2026-09-12')`, que é
 * meia-noite UTC e volta um dia atrás no `DatePicker` de quem está em UTC-3.
 */
function referenceDateToLocalDate(referenceDate: string): Date | null {
  if (!referenceDate) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(referenceDate)
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  }
  const parsed = new Date(referenceDate)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * §8.2 — "Empresa / Unidade / Data do plantão / Escala + Modalidade
 * pré-preenchidas **e bloqueadas** a partir da vaga". Só bloqueia o que de fato
 * veio: um payload sem `companyId` deixa o campo editável em vez de travar o
 * emissor num wizard vazio.
 */
export function buildSheetHandoff(payload: RedirectToSheetPayload): SheetHandoff {
  const form: Partial<DsAttendanceUploadForm> = {}
  const lockedFields: string[] = []

  if (payload.companyId) {
    form.company = payload.companyId
    lockedFields.push('company')
  }
  if (payload.healthUnitId) {
    form.healthUnit = payload.healthUnitId
    lockedFields.push('healthUnit')
  }

  const date = referenceDateToLocalDate(payload.referenceDate)
  if (date) {
    form.date = date
    lockedFields.push('date')
  }

  if (payload.scheduleId) {
    form.schedules = [payload.scheduleId]
    lockedFields.push('schedules')
  }

  // O turno vem MARCADO, mas não travado: a mesma folha costuma cobrir os outros
  // turnos da escala, e o emissor precisa poder acrescentá-los.
  if (payload.shiftId) {
    form.shifts = [payload.shiftId]
  }

  if (payload.monitoringType) {
    // A modalidade não é campo do passo 1: ela vem da escala e só rotula o preview.
    form.monitoringType = payload.monitoringType
    form.monitoringTypeLabel = formatMonitoringTypeLabel(payload.monitoringType)
  }

  return { form, lockedFields }
}

/* ------------------------------------------------------------------------- *
 * Estado da equivalência
 * ------------------------------------------------------------------------- */

/** Uma linha da tabela do passo 4: o que ESTE registro declara para UM plantão. */
export interface SendRecordLine {
  doctorStationId: number
  coveredLegs: CoveredLegs
  declaredCheckin: string | null
  declaredCheckout: string | null
  /**
   * Offset em que os horários desta linha sao declarados e enviados (`-03:00`).
   * Nasce do fuso da ESCALA (prefill) e so muda se o emissor escolher outro no
   * seletor do passo 4 — nunca o fuso do navegador.
   */
  timezone: string
}

export interface PrefillEffective {
  checkin: string | null
  checkout: string | null
  checkinSource: EffectiveSourceKind | null
  checkoutSource: EffectiveSourceKind | null
  registrationStatus: EffectiveRegistrationStatus
  frozen: boolean
}

/** Item do prefill já normalizado — a tela lê SÓ deste formato. */
export interface NormalizedPrefillItem {
  doctorStationId: number
  doctorName: string | null
  scheduleName: string | null
  healthUnitName: string | null
  expectedStart: string | null
  expectedEnd: string | null
  effective: PrefillEffective
  suggestedCoveredLegs: CoveredLegs
  acceptedActivityTypes: ActivityType[] | null
  checkinLateToleranceMinutes: number | null
  checkoutEarlyToleranceMinutes: number | null
  /** Fuso da escala/unidade do plantao (`-03:00`), como o backend o persiste. */
  timezone: string
}

/** Um registro criado, para a tela de sucesso. */
export interface CreatedActivityRecord {
  containerId: number
  doctorStationId: number | null
  status: string | null
}

/* ------------------------------------------------------------------------- *
 * Instância exposta aos specs (padrão de `AttendanceUploadModal/types.ts`)
 * ------------------------------------------------------------------------- */

export interface SendRecordModalInstance {
  currentStep: number
  category: SendRecordCategory | null
  sheetMonitoringType: DSAttendanceMonitoringType | null
  activitySubtype: ActivitySubtype
  selectedDoctorStationIds: number[]
  activityType: ActivityType | null
  description: string
  files: File[]
  fileErrors: string[]
  selfCheck: ActivitySelfCheckState
  prefill: NormalizedPrefillItem[]
  lines: SendRecordLine[]
  truthDeclaration: boolean
  isSaving: boolean
  errorCode: string | null
  errorMessage: string | null
  warnings: ActivityRecordWarning[]
  createdRecords: CreatedActivityRecord[]
  isNextDisabled: boolean
  goNext(): Promise<void>
  goBack(): void
  submit(): Promise<void>
}

/* ------------------------------------------------------------------------- *
 * Helpers puros — horários
 * ------------------------------------------------------------------------- */

/**
 * `2026-09-12T19:00:00-03:00` → `19:00`, LIDO NO FUSO DA ESCALA. Já aceita
 * `HH:mm` de volta.
 *
 * O fuso é sempre o do plantão (padrão Brasília quando a escala não o informa),
 * nunca o do navegador: `getHours()` fazia o mesmo registro aparecer 08:00 aqui
 * e 11:00 num navegador em UTC.
 */
export function isoToTimeInput(
  value: string | null | undefined,
  offset: string = DEFAULT_UTC_OFFSET
): string {
  if (!value) return ''
  if (/^\d{2}:\d{2}$/.test(value)) return value
  return formatHourMinuteInOffset(value, offset) ?? ''
}

/**
 * Aplica um `HH:mm` digitado sobre a data do plantão, mantendo o dia NO FUSO DA
 * ESCALA. Ida e volta no mesmo offset, então `isoToTimeInput` do resultado
 * devolve exatamente o que o usuário digitou — em qualquer navegador.
 */
export function applyTimeInputToIso(
  base: string | null | undefined,
  time: string,
  offset: string = DEFAULT_UTC_OFFSET
): string | null {
  return buildIsoAtOffset(base, time, offset)
}

/** Diferença em minutos (`b - a`); `null` quando qualquer lado falta. */
export function minutesBetween(
  a: string | null | undefined,
  b: string | null | undefined
): number | null {
  if (!a || !b) return null
  const first = new Date(a).getTime()
  const second = new Date(b).getTime()
  if (Number.isNaN(first) || Number.isNaN(second)) return null
  return Math.round((second - first) / 60000)
}

/** `true` quando falta o horário desta perna E ela é coberta por este registro. */
export function isLegTimeMissing(line: SendRecordLine, leg: CoveredLegs): boolean {
  if (leg === CoveredLegs.CHECKIN) {
    return line.coveredLegs !== CoveredLegs.CHECKOUT && !line.declaredCheckin
  }
  return line.coveredLegs !== CoveredLegs.CHECKIN && !line.declaredCheckout
}

/**
 * `true` quando falta o horário de alguma perna que ESTA linha diz cobrir. Uma
 * perna coberta sem horário viajaria como "declaro o check-in" sem check-in
 * nenhum, e o backend recusaria (`ACTIVITY_LEGS_INVALID`) depois do upload.
 */
export function isMissingDeclaredTime(line: SendRecordLine): boolean {
  return isLegTimeMissing(line, CoveredLegs.CHECKIN) || isLegTimeMissing(line, CoveredLegs.CHECKOUT)
}

export interface ToleranceWarning {
  leg: 'CHECKIN' | 'CHECKOUT'
  minutes: number
  text: string
}

/**
 * Aviso de tolerância (A4, balão ⑳). NUNCA bloqueia: existe para o emissor
 * corrigir um erro de digitação antes de gerar trabalho na fila. Sem tolerância
 * configurada na política, não há régua — e um aviso sem régua seria ruído.
 */
function coversLeg(line: SendRecordLine, leg: CoveredLegs): boolean {
  return line.coveredLegs === leg || line.coveredLegs === CoveredLegs.BOTH
}

/** Um aviso, quando a diferença passa da régua da política. */
function toleranceWarning(params: {
  leg: 'CHECKIN' | 'CHECKOUT'
  covered: boolean
  tolerance: number | null
  diff: number | null
  declared: string | null
  expected: string | null
  offset: string
}): ToleranceWarning | null {
  if (!params.covered || params.tolerance === null) return null
  if (params.diff === null || params.diff <= params.tolerance) return null

  const direction = params.leg === 'CHECKIN' ? 'depois' : 'antes'
  return {
    leg: params.leg,
    minutes: params.diff,
    text:
      `${isoToTimeInput(params.declared, params.offset)} está ${params.diff} minutos ` +
      `${direction} do previsto (${isoToTimeInput(params.expected, params.offset)})`
  }
}

export function buildToleranceWarnings(params: {
  line: SendRecordLine
  expectedStart: string | null
  expectedEnd: string | null
  checkinLateToleranceMinutes: number | null
  checkoutEarlyToleranceMinutes: number | null
}): ToleranceWarning[] {
  return [
    toleranceWarning({
      leg: 'CHECKIN',
      covered: coversLeg(params.line, CoveredLegs.CHECKIN),
      tolerance: params.checkinLateToleranceMinutes,
      diff: minutesBetween(params.expectedStart, params.line.declaredCheckin),
      declared: params.line.declaredCheckin,
      expected: params.expectedStart,
      offset: params.line.timezone
    }),
    toleranceWarning({
      leg: 'CHECKOUT',
      covered: coversLeg(params.line, CoveredLegs.CHECKOUT),
      tolerance: params.checkoutEarlyToleranceMinutes,
      diff: minutesBetween(params.line.declaredCheckout, params.expectedEnd),
      declared: params.line.declaredCheckout,
      expected: params.expectedEnd,
      offset: params.line.timezone
    })
  ].filter((warning): warning is ToleranceWarning => warning !== null)
}

/* ------------------------------------------------------------------------- *
 * Helpers puros — prefill
 * ------------------------------------------------------------------------- */

type RawRecord = Record<string, unknown>

function asRecord(value: unknown): RawRecord {
  return value && typeof value === 'object' ? (value as RawRecord) : {}
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null
}

function asNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** Perna que falta (§2.4). `BOTH` quando faltam as duas — e quando já está OK. */
export function suggestCoveredLegs(effective: PrefillEffective): CoveredLegs {
  if (effective.registrationStatus === EffectiveRegistrationStatus.OK) return CoveredLegs.BOTH
  if (effective.checkin && !effective.checkout) return CoveredLegs.CHECKOUT
  if (!effective.checkin && effective.checkout) return CoveredLegs.CHECKIN
  return CoveredLegs.BOTH
}

/**
 * Normaliza um item do prefill. Aceita as DUAS formas do contrato:
 * a do spec §2.4 (`expectedStart`/`expectedEnd` + `effective` aninhado) e a que
 * a camada de rede da W1 declarou (`startHour`/`endHour` + campos achatados).
 * O backend da F2 ainda não existe — normalizar aqui é o que impede a tela de
 * quebrar quando ele chegar com uma das duas.
 */
function normalizeEffective(item: RawRecord): PrefillEffective {
  const nested = asRecord(item.effective)
  // A forma ANINHADA é a do contrato (§2.4, confirmada em B17 §1.1). A achatada é a
  // suposição da W1, que o backend nunca serviu: fica só como rede de segurança para
  // objetos montados à mão (`prefillFromStationOption`, mocks), sem custo em produção.
  const source: RawRecord = Object.keys(nested).length
    ? nested
    : {
        checkin: item.effectiveCheckin,
        checkout: item.effectiveCheckout,
        checkinSource: item.checkinSource,
        checkoutSource: item.checkoutSource,
        registrationStatus: item.registrationStatus,
        frozen: item.frozen
      }

  return {
    checkin: asString(source.checkin),
    checkout: asString(source.checkout),
    checkinSource: normalizeSource(source.checkinSource),
    checkoutSource: normalizeSource(source.checkoutSource),
    registrationStatus: normalizeRegistrationStatus(source.registrationStatus),
    frozen: Boolean(source.frozen)
  }
}

export function normalizePrefillItem(raw: unknown): NormalizedPrefillItem | null {
  const item = asRecord(raw)
  const doctorStationId = asNumberOrNull(item.doctorStationId)
  if (doctorStationId === null) return null

  const effective = normalizeEffective(item)
  const policy = asRecord(item.policy)
  const accepted = validateActivityTypes(policy.acceptedActivityTypes)

  const suggested = Object.values(CoveredLegs).includes(item.suggestedCoveredLegs as CoveredLegs)
    ? (item.suggestedCoveredLegs as CoveredLegs)
    : suggestCoveredLegs(effective)

  return {
    doctorStationId,
    doctorName: asString(item.doctorName),
    scheduleName: asString(item.scheduleName),
    healthUnitName: asString(item.healthUnitName),
    expectedStart: asString(item.expectedStart) ?? asString(item.startHour),
    expectedEnd: asString(item.expectedEnd) ?? asString(item.endHour),
    effective,
    suggestedCoveredLegs: suggested,
    acceptedActivityTypes: accepted,
    checkinLateToleranceMinutes: asNumberOrNull(policy.checkinLateToleranceMinutes),
    checkoutEarlyToleranceMinutes: asNumberOrNull(policy.checkoutEarlyToleranceMinutes),
    // Backend sem o campo cai em Brasilia — o mesmo fallback `timezone || '-03:00'`
    // que as outras telas do plantao ja usam.
    timezone: asString(item.timezone) ?? DEFAULT_UTC_OFFSET
  }
}

function normalizeSource(value: unknown): EffectiveSourceKind | null {
  const kind = typeof value === 'object' && value ? asRecord(value).kind : value
  return Object.values(EffectiveSourceKind).includes(kind as EffectiveSourceKind)
    ? (kind as EffectiveSourceKind)
    : null
}

function normalizeRegistrationStatus(value: unknown): EffectiveRegistrationStatus {
  const isValid = Object.values(EffectiveRegistrationStatus).includes(
    value as EffectiveRegistrationStatus
  )
  if (!isValid && value !== undefined && value !== null) {
    console.warn('[SendRecordModal] Status de registro desconhecido:', value)
  }
  return isValid ? (value as EffectiveRegistrationStatus) : EffectiveRegistrationStatus.NONE
}

function validateActivityTypes(types: unknown): ActivityType[] | null {
  if (!types) return null
  if (!Array.isArray(types)) return null
  const validTypes = Object.values(ActivityType)
  const isValid = types.every((t) => validTypes.includes(t as ActivityType))
  if (!isValid) {
    console.warn('[SendRecordModal] Alguns tipos de atividade são desconhecidos:', types)
  }
  return isValid ? (types as ActivityType[]) : null
}

/**
 * `suggestedCoveredLegs` do contrato de candidatos (BF16) aceita `NONE` — que
 * NÃO existe em `CoveredLegs` (§2.4 só conhece a perna que falta). `null` deixa
 * `prefillFromStationOption` decidir pelo `suggestCoveredLegs(effective)`.
 */
function normalizeCandidateCoveredLegs(value: unknown): CoveredLegs | null {
  return Object.values(CoveredLegs).includes(value as CoveredLegs) ? (value as CoveredLegs) : null
}

/**
 * BF16 — um candidato de "Adicionar outros plantões" (§2 do wizard) vira a MESMA
 * forma que a vaga de origem: dead code do `SendRecordContext.otherStations`
 * (nunca populado) sai substituído pela rota
 * `GET .../activity-records/candidates`, que o passo 2 busca sozinho.
 */
export function candidateToStationOption(
  candidate: ActivityRecordCandidateStation
): SendRecordStationOption {
  return {
    doctorStationId: candidate.doctorStationId,
    referenceDate: candidate.date,
    expectedStart: candidate.expectedStart,
    expectedEnd: candidate.expectedEnd,
    scheduleId: candidate.scheduleId,
    scheduleName: candidate.scheduleName,
    healthUnitName: candidate.healthUnitName,
    registrationStatus: normalizeRegistrationStatus(candidate.registrationStatus),
    missingLegs: normalizeCandidateCoveredLegs(candidate.suggestedCoveredLegs),
    timezone: candidate.timezone ?? DEFAULT_UTC_OFFSET,
    hasOpenActivityRecord: Boolean(candidate.hasOpenActivityRecord)
  }
}

/** Item sintético para quando o prefill não existe (backend antigo) ou falhou. */
export function prefillFromStationOption(option: SendRecordStationOption): NormalizedPrefillItem {
  const effective: PrefillEffective = {
    checkin: null,
    checkout: null,
    checkinSource: null,
    checkoutSource: null,
    registrationStatus: option.registrationStatus ?? EffectiveRegistrationStatus.NONE,
    frozen: false
  }
  return {
    doctorStationId: option.doctorStationId,
    doctorName: option.doctorName ?? null,
    scheduleName: option.scheduleName ?? null,
    healthUnitName: option.healthUnitName ?? null,
    expectedStart: option.expectedStart ?? null,
    expectedEnd: option.expectedEnd ?? null,
    effective,
    suggestedCoveredLegs: option.missingLegs ?? suggestCoveredLegs(effective),
    acceptedActivityTypes: null,
    checkinLateToleranceMinutes: null,
    checkoutEarlyToleranceMinutes: null,
    timezone: option.timezone ?? DEFAULT_UTC_OFFSET
  }
}

/** Linha inicial do passo 4 — horário PREVISTO, nunca o efetivo (Q5). */
export function buildLineFromPrefill(item: NormalizedPrefillItem): SendRecordLine {
  return {
    doctorStationId: item.doctorStationId,
    coveredLegs: item.suggestedCoveredLegs,
    declaredCheckin: item.expectedStart,
    declaredCheckout: item.expectedEnd,
    timezone: item.timezone
  }
}

/* ------------------------------------------------------------------------- *
 * Helpers puros — anexos
 * ------------------------------------------------------------------------- */

export interface AttachmentValidationResult {
  accepted: File[]
  errors: string[]
}

/**
 * Mimes de V7 (§2.3). `image/jpg` não é oficial, mas alguns scanners o emitem.
 * Vive aqui, e não em `ds-attendance.constants.ts`, porque só o emissor de M2
 * valida anexo por mime hoje.
 */
const ACTIVITY_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg'
] as const

/**
 * Mimes que não dizem nada: câmeras e gerenciadores de arquivo mobile entregam
 * `File` assim. Recusá-los seria pior do que confiar na extensão.
 */
const OPAQUE_MIME_TYPES = ['', 'application/octet-stream', 'binary/octet-stream']

/**
 * I16 — V7 fala em **mime**; a extensão é só o fallback de quem não manda mime.
 * Um `.jpg` que na verdade é um executável morre aqui, e não depois do upload.
 */
function hasAcceptedFormat(file: File): boolean {
  const mime = (file.type ?? '').toLowerCase().trim()

  if (!OPAQUE_MIME_TYPES.includes(mime)) {
    return ACTIVITY_ATTACHMENT_MIME_TYPES.includes(
      mime as (typeof ACTIVITY_ATTACHMENT_MIME_TYPES)[number]
    )
  }

  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  return (
    file.name.includes('.') &&
    ACTIVITY_ATTACHMENT_LIMITS.allowedExtensions.includes(extension as never)
  )
}

export function formatFileSize(bytes: number): string {
  if (!bytes) return '0 KB'
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${mb.toFixed(1).replace('.', ',')} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/**
 * Filtra os arquivos aceitos e devolve a mensagem de cada recusa (M2, erro A).
 * O que já estava anexado NÃO é perdido — quem chama concatena `accepted`.
 */
export function validateAttachments(
  incoming: File[],
  alreadyAttached: File[]
): AttachmentValidationResult {
  const accepted: File[] = []
  const errors: string[] = []
  let slots = ACTIVITY_ATTACHMENT_LIMITS.maxFiles - alreadyAttached.length
  let total = alreadyAttached.reduce((sum, file) => sum + file.size, 0)

  for (const file of incoming) {
    if (!hasAcceptedFormat(file)) {
      errors.push(`${file.name}: formato não aceito (use PDF, PNG ou JPG)`)
      continue
    }
    if (file.size > ACTIVITY_ATTACHMENT_LIMITS.maxFileBytes) {
      errors.push(`${file.name}: arquivo acima de 50 MB. Comprima o arquivo ou divida em dois.`)
      continue
    }
    if (slots <= 0) {
      errors.push(`${file.name}: limite de ${ACTIVITY_ATTACHMENT_LIMITS.maxFiles} anexos atingido`)
      continue
    }
    if (total + file.size > ACTIVITY_ATTACHMENT_LIMITS.maxTotalBytes) {
      errors.push(`${file.name}: a soma dos anexos passa de 200 MB`)
      continue
    }
    accepted.push(file)
    slots -= 1
    total += file.size
  }

  return { accepted, errors }
}

/* ------------------------------------------------------------------------- *
 * Helpers puros — validação de passo e catálogo de erros
 * ------------------------------------------------------------------------- */

/** `ADMIN_ADJUSTMENT` dispensa anexo (V7b) e move a justificativa para o passo 4. */
export function requiresAttachment(activityType: ActivityType | null): boolean {
  return activityType !== ActivityType.ADMIN_ADJUSTMENT
}

export function requiresDescriptionOnTypeStep(activityType: ActivityType | null): boolean {
  return activityType === ActivityType.OTHER
}

export function isDescriptionLongEnough(description: string): boolean {
  return description.trim().length >= ACTIVITY_DESCRIPTION_MIN_LENGTH
}

/**
 * Catálogo de erros do envio (§2.7). A tela escolhe o texto pelo `code` — a
 * `message` do backend só entra quando o código é desconhecido.
 */
export const ACTIVITY_ERROR_MESSAGES: Record<string, string> = {
  ACTIVITY_RECORDS_DISABLED:
    'Esta escala não aceita Registro de Atividade. Peça para a configuração ser liberada na ' +
    'Política de Registro de Plantão.',
  ACTIVITY_TYPE_NOT_ACCEPTED: 'Este tipo não está liberado para a escala selecionada.',
  ACTIVITY_STATION_WITHOUT_DOCTOR: 'Um dos plantões selecionados ainda não tem médico alocado.',
  ACTIVITY_STATION_OUT_OF_SCOPE: 'Um dos plantões selecionados não pertence a esta escala.',
  ACTIVITY_STATION_NOT_FOUND: 'Um dos plantões selecionados não existe mais.',
  ACTIVITY_RECORD_ALREADY_OPEN:
    'Já existe um Registro de Atividade aguardando auditoria para este plantão.',
  ACTIVITY_DESCRIPTION_REQUIRED: 'Descreva o que aconteceu com pelo menos 20 caracteres.',
  ACTIVITY_ATTACHMENT_INVALID: 'Um dos anexos não foi aceito (use PDF, PNG ou JPG, até 50 MB).',
  ACTIVITY_LEGS_INVALID: 'A perna declarada não confere com os horários informados.',
  ACTIVITY_RECORD_DUPLICATE: 'Este envio já foi registrado.'
}

/** O único erro com link para a fila: o registro em aberto já está lá. */
export const ACTIVITY_ERROR_WITH_QUEUE_LINK = 'ACTIVITY_RECORD_ALREADY_OPEN'

/**
 * Timeout: o POST tem 120 s e o envio pode ter concluído do outro lado. Sugerir
 * reenvio cego criaria um segundo registro para o mesmo plantão.
 */
export const ACTIVITY_TIMEOUT_MESSAGE =
  'O envio demorou mais do que o esperado e pode ter concluído; verifique a fila antes de reenviar.'
